import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Briefcase, Headphones, Share2, PenTool, Settings, Target,
  Award, DollarSign, TrendingUp, Shield, Check, Star,
  Mail, Phone, MapPin, Menu, X, ChevronLeft, ChevronRight,
  FileCheck, ClipboardList, Receipt, CalendarClock, ShieldCheck,
  FileSignature, SearchCheck, HeartHandshake
} from 'lucide-react';
import { services, pasaServices, pricingPlans, testimonials, stats, whyChooseUs, submitContactForm } from '../mock';
import { useToast } from '../hooks/use-toast';

const iconMap = {
  Briefcase, Headphones, Share2, PenTool, Settings, Target,
  Award, DollarSign, TrendingUp, Shield
};

const pasaIconMap = {
  FileCheck, ClipboardList, Receipt, CalendarClock, ShieldCheck,
  Briefcase, FileSignature, SearchCheck
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitContactForm(formData);
      toast({
        title: "Success!",
        description: result.message,
      });
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="home-container">
      {/* Header Navigation */}
      <header className="header-nav">
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1280px', margin: '0 auto', padding: '0 1.2rem' }}>
          <div className="logo-container" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_assist-virtual-2/artifacts/xj90edy5_image.png" 
              alt="Virtual Enterprise LTD Logo" 
              className="company-logo"
            />
            <h2 className="company-name">
              Virtual Enterprise LTD
            </h2>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="desktop-nav" style={{ display: 'none' }}>
            <Button variant="ghost" onClick={() => scrollToSection('services')} className="nav-link">Services</Button>
            <Button variant="ghost" onClick={() => scrollToSection('pasa')} className="nav-link">PASA Support</Button>
            <Button variant="ghost" onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</Button>
            <Button variant="ghost" onClick={() => scrollToSection('testimonials')} className="nav-link">Testimonials</Button>
            <Button variant="ghost" onClick={() => scrollToSection('contact')} className="nav-link">Contact</Button>
          </nav>

          <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button onClick={() => scrollToSection('contact')} className="btn-primary desktop-only">
              Get Started
            </Button>
            <button 
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="mobile-menu">
            <Button variant="ghost" onClick={() => scrollToSection('services')} className="mobile-nav-link">Services</Button>
            <Button variant="ghost" onClick={() => scrollToSection('pasa')} className="mobile-nav-link">PASA Support</Button>
            <Button variant="ghost" onClick={() => scrollToSection('pricing')} className="mobile-nav-link">Pricing</Button>
            <Button variant="ghost" onClick={() => scrollToSection('testimonials')} className="mobile-nav-link">Testimonials</Button>
            <Button variant="ghost" onClick={() => scrollToSection('contact')} className="mobile-nav-link">Contact</Button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-announcement">
            <Star size={14} />
            <span>Starting at $8/hour - Up to 40 hours/week</span>
          </div>
          <h1 className="heading-hero">
            Professional Virtual Assistance for Your Growing Business
          </h1>
          <p className="body-large hero-subtitle">
            Scale your operations with expert virtual assistants. From administrative support to technical services, we handle it all so you can focus on what matters most.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2rem' }}>
            <Button onClick={() => scrollToSection('contact')} className="btn-primary">
              Get Started Today
            </Button>
            <Button onClick={() => scrollToSection('services')} className="btn-secondary">
              Explore Services
            </Button>
          </div>

          {/* Stats */}
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item">
                <div className="heading-2">{stat.value}</div>
                <div className="caption">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Virtual Services</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0.5rem auto 0' }}>
              Comprehensive virtual assistance tailored to your business needs
            </p>
          </div>

          <div className="voice-grid">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className={`voice-card ${service.accentClass}`}>
                  <CardHeader>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <IconComponent size={32} color="var(--text-primary)" />
                    </div>
                    <CardTitle className="voice-card-title">{service.title}</CardTitle>
                    <CardDescription className="voice-card-description">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {service.features.map((feature, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                          <Check size={16} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PASA Support Section */}
      <section id="pasa" className="pasa-section section-padding">
        <div className="container">
          <div className="pasa-wrapper">
            <div className="pasa-intro" data-testid="pasa-intro">
              <div className="pasa-badge">
                <HeartHandshake size={14} />
                <span>For PASA Owners & Founders</span>
              </div>
              <h2 className="heading-1 pasa-question">
                If CDPHE or HCPF walked into your PASA tomorrow, would your documentation, HRCs, ISPs, and compliance records be ready?
              </h2>
              <p className="body-large pasa-lead">
                Virtual Enterprise LTD specializes in supporting PASA owners with the administrative and compliance work that keeps operations running smoothly and survey-ready.
              </p>
              <div className="pasa-founder">
                <Award size={20} />
                <p className="body-medium" style={{ margin: 0 }}>
                  Backed by our founder's <strong>30+ years of PASA experience</strong>, we understand exactly what it takes to operate a successful, compliant agency.
                </p>
              </div>
              <Button onClick={() => scrollToSection('contact')} className="btn-primary" data-testid="pasa-cta-button">
                Schedule a Conversation
              </Button>
            </div>

            <div className="pasa-services" data-testid="pasa-services-list">
              <h3 className="heading-3 pasa-services-title">How we help your PASA</h3>
              <div className="pasa-grid">
                {pasaServices.map((item, idx) => {
                  const IconComponent = pasaIconMap[item.icon];
                  return (
                    <div key={idx} className="pasa-item" data-testid={`pasa-item-${idx}`}>
                      <div className="pasa-item-icon">
                        <IconComponent size={20} />
                      </div>
                      <span className="body-small pasa-item-text">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-section section-padding" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Choose Virtual Enterprise LTD</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0.5rem auto 0' }}>
              We deliver excellence through our commitment to quality and customer satisfaction
            </p>
          </div>

          <div className="ai-grid">
            {whyChooseUs.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <Card key={index} className="hover-lift">
                  <CardHeader>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <IconComponent size={28} color="var(--text-primary)" />
                    </div>
                    <CardTitle className="heading-3">{item.title}</CardTitle>
                    <CardDescription className="body-medium">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Flexible Pricing Plans</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0.5rem auto 0' }}>
              Choose the plan that fits your business needs. All plans include our quality guarantee.
            </p>
          </div>

          <div className="pricing-grid">
            {pricingPlans.map((plan) => (
              <Card key={plan.id} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <CardHeader>
                  <CardTitle className="heading-2">{plan.name}</CardTitle>
                  <div style={{ margin: '1rem 0' }}>
                    <span className="heading-hero" style={{ fontSize: '2.5rem' }}>${plan.price}</span>
                    <span className="body-small" style={{ color: 'var(--text-muted)' }}>/{plan.period}</span>
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-primary)', fontWeight: '500', marginBottom: '0.5rem' }}>
                    {plan.weeklyLimit}
                  </div>
                  <CardDescription className="body-medium">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '1.5rem 0' }}>
                    {plan.features.map((feature, idx) => (
                      <li key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <Check size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
                        <span className="body-small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => scrollToSection('contact')} 
                    className={plan.popular ? 'btn-primary' : 'btn-secondary'}
                    style={{ width: '100%' }}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section section-padding" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">What Our Clients Say</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Trusted by businesses worldwide
            </p>
          </div>

          <div className="testimonial-carousel">
            <Card className="testimonial-card">
              <CardContent style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem' }}>
                  {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                    <Star key={i} size={18} fill="currentColor" />
                  ))}
                </div>
                <p className="body-large" style={{ marginBottom: '1.5rem', fontStyle: 'italic' }}>
                  "{testimonials[currentTestimonial].content}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div className="avatar">
                    {testimonials[currentTestimonial].avatar}
                  </div>
                  <div>
                    <div className="heading-3" style={{ marginBottom: '0.25rem' }}>
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="body-small" style={{ color: 'var(--text-muted)' }}>
                      {testimonials[currentTestimonial].role}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="carousel-controls">
              <button onClick={prevTestimonial} className="btn-nav">
                <ChevronLeft size={20} />
              </button>
              <button onClick={nextTestimonial} className="btn-nav">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section section-padding">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info">
              <h2 className="heading-1">Get in Touch</h2>
              <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Ready to transform your business with professional virtual assistance? Contact us today for a free consultation.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <Mail size={24} />
                  <div>
                    <div className="heading-3">Email</div>
                    <div className="body-medium">virtualenterpriseltd@gmail.com</div>
                  </div>
                </div>
                <div className="contact-item">
                  <Phone size={24} />
                  <div>
                    <div className="heading-3">Phone</div>
                    <div className="body-medium">+1 (720) 277-9238</div>
                  </div>
                </div>
                <div className="contact-item">
                  <MapPin size={24} />
                  <div>
                    <div className="heading-3">Location</div>
                    <div className="body-medium">2000 S Colorado Blvd.<br />BLDG 1-2000 - #500<br />Denver, CO, 80222</div>
                  </div>
                </div>
              </div>
            </div>

            <Card className="contact-form-card">
              <CardHeader>
                <CardTitle className="heading-2">Send Us a Message</CardTitle>
                <CardDescription>We'll respond within 24 hours</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <Input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <Input
                      type="text"
                      name="service"
                      placeholder="Service Interested In"
                      value={formData.service}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <Textarea
                      name="message"
                      placeholder="Tell us about your needs..."
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      required
                    />
                  </div>
                  <Button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_assist-virtual-2/artifacts/xj90edy5_image.png" 
                  alt="Virtual Enterprise LTD Logo" 
                  className="footer-logo"
                />
                <h3 className="heading-3" style={{ color: 'white', marginTop: '0.5rem' }}>Virtual Enterprise LTD</h3>
              </div>
              <p className="body-small" style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.75rem' }}>
                Professional virtual assistance services for businesses worldwide.
              </p>
            </div>
            <div className="footer-section">
              <h4 className="heading-3">Quick Links</h4>
              <ul className="footer-links">
                <li><button onClick={() => scrollToSection('services')}>Services</button></li>
                <li><button onClick={() => scrollToSection('pricing')}>Pricing</button></li>
                <li><button onClick={() => scrollToSection('testimonials')}>Testimonials</button></li>
                <li><button onClick={() => scrollToSection('contact')}>Contact</button></li>
              </ul>
            </div>
            <div className="footer-section">
              <h4 className="heading-3">Services</h4>
              <ul className="footer-links">
                <li>PASA Compliance Support</li>
                <li>Administrative Support</li>
                <li>Customer Service</li>
                <li>Social Media Management</li>
                <li>Content Creation</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="caption">© 2024 Virtual Enterprise LTD. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
